import { useState } from 'react';
import { Ball } from './Ball.js';
import { countOf, fmtMoney, type GoldenBallsViewerState, type GbSeatView } from './types.js';

// ---------------------------------------------------------------------------
// Bin or Win
// ---------------------------------------------------------------------------
// Alternate placing balls until the Win row holds five. A killer placed
// in Win divides the running total by ten AT THAT MOMENT — the total is
// the loudest thing on screen, and the division is shown as it happens.
//
// The eleven balls are SHUFFLED and face down. You know exactly what
// went into the pool (the composition is listed) but not which slot
// holds what, so a placement is a real gamble instead of picking a
// number off a list.

export function BinOrWin({
  view,
  seats,
  yourSeatId,
  busy,
  onPlace,
  onSkipToEnd
}: {
  view: GoldenBallsViewerState;
  seats: GbSeatView[];
  yourSeatId: string | null;
  busy: boolean;
  onPlace: (slot: number, dest: 'bin' | 'win') => void;
  /** Both finalists must ask before the rest resolves at once. */
  onSkipToEnd: () => void;
}) {
  const binwin = view.binwin;
  const [selected, setSelected] = useState<number | null>(null);
  if (!binwin) return null;

  const label = (seatId: string | null) =>
    seatId ? (seats.find((seat) => seat.seatId === seatId)?.label ?? seatId) : '—';
  const yourTurn = binwin.currentPlacer !== null && binwin.currentPlacer === yourSeatId;
  const binFull = binwin.binRow.length >= binwin.binCapacity;
  const lastPlacement = binwin.placements[binwin.placements.length - 1];
  const killerJustHit = lastPlacement?.dest === 'win' && lastPlacement.revealed === 'KILLER';
  const unplaced = binwin.slots.filter((slot) => !slot.placed);
  const isFinalist = !!yourSeatId && binwin.order.includes(yourSeatId);
  const youAskedToSkip = !!yourSeatId && binwin.skipToEndSeatIds.includes(yourSeatId);
  const otherFinalist = binwin.order.find((seatId) => seatId !== yourSeatId) ?? null;

  return (
    <div className="flex flex-col gap-3">
      {/* Running total — the loudest thing on screen. */}
      <div className="shrink-0 rounded-lg border border-ec-line bg-ec-panel p-3 text-center">
        <p className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
          Win row total
        </p>
        <p
          className={`font-display text-4xl font-bold tabular-nums ${
            killerJustHit
              ? 'animate-pulse text-[#e02a1f] motion-reduce:animate-none'
              : 'text-ec-brass'
          }`}
        >
          {fmtMoney(binwin.runningTotal)}
        </p>
        {killerJustHit && (
          <p className="font-display text-[0.65rem] uppercase tracking-wide text-[#e02a1f]">
            killer — divided by ten
          </p>
        )}
        <p className="mt-1 font-serif text-xs text-ec-bone-dim">
          Win {binwin.winRow.length}/5 · Bin {binwin.binRow.length}/{binwin.binCapacity}
          {binFull && ' · bin full, everything goes to Win'}
        </p>
      </div>

      <div className="flex shrink-0 flex-col gap-2">
        <Row title="Win" tone="brass" entries={binwin.winRow.map((e) => e.revealed)} capacity={5} />
        <Row title="Bin" tone="slate" entries={binwin.binRow.map((e) => e.revealed)} capacity={binwin.binCapacity} />
      </div>

      <p className="shrink-0 text-center font-display text-xs uppercase tracking-wide text-ec-bone">
        {yourTurn ? 'Your placement — pick a ball' : `${label(binwin.currentPlacer)} is placing…`}
      </p>

      {/* Blind slots. Sized to CONTENT — the column scrolls, this does
          not. As its own flex-1 scroller nested inside another one, the
          last six balls ended up painted past the clip line with no way
          to scroll to them: unplaceable balls in a placement phase. */}
      <div className="shrink-0 rounded-lg border border-ec-line bg-ec-panel p-2">
        <p className="mb-2 font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
          {countOf(unplaced.length, 'ball')} left — face down
        </p>
        <div className="flex flex-wrap gap-2">
          {binwin.slots.map((slot) =>
            slot.placed ? (
              <Ball
                key={slot.slot}
                claim={slot.revealed}
                size="sm"
                dimmed
                label={slot.dest === 'win' ? 'won' : 'binned'}
              />
            ) : (
              <Ball
                key={slot.slot}
                claim={null}
                state="faceDown"
                selected={selected === slot.slot}
                label={`#${slot.slot + 1}`}
                onClick={yourTurn && !busy ? () => setSelected(slot.slot) : undefined}
                title="Nobody knows what this is until it's placed"
              />
            )
          )}
        </div>

        {/* What went in — public knowledge, so the gamble is informed. */}
        <div className="mt-3 border-t border-ec-line pt-2">
          <p className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
            What went into the pool
          </p>
          <p className="mt-1 font-serif text-xs text-ec-bone">
            {binwin.composition.cashValues.map((value) => fmtMoney(value)).join(' · ')}
            {binwin.composition.killers > 0 && (
              <span className="text-[#e02a1f]">
                {' '}
                · {countOf(binwin.composition.killers, 'killer')}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Pinned actions — never buried under the pool on a phone.
          `sticky` rather than a plain row: the column scrolls now, and
          these have to stay on screen while you scroll the pool. */}
      {yourTurn && (
        <div className="sticky bottom-0 z-10 flex shrink-0 gap-2 bg-ec-ground/95 py-1">
          <button
            type="button"
            disabled={selected === null || busy}
            onClick={() => selected !== null && onPlace(selected, 'win')}
            className="ec-btn ec-btn--gold min-h-[3rem] flex-1 text-sm disabled:opacity-40"
          >
            {selected === null ? 'Pick a ball' : `Ball #${selected + 1} → WIN`}
          </button>
          <button
            type="button"
            disabled={selected === null || busy || binFull}
            onClick={() => selected !== null && onPlace(selected, 'bin')}
            className="ec-btn min-h-[3rem] flex-1 text-sm disabled:opacity-40"
          >
            {binFull ? 'Bin full' : selected === null ? '—' : `Ball #${selected + 1} → BIN`}
          </button>
        </div>
      )}

      {/* Fast-forward the rest. Offered to both finalists at any point —
          it only fires once BOTH have asked, so nobody can hand away
          the other player's remaining placements. */}
      {isFinalist && unplaced.length > 0 && (
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            disabled={busy || youAskedToSkip}
            onClick={onSkipToEnd}
            className="ec-btn min-h-[2.5rem] px-3 text-xs disabled:opacity-40"
          >
            {youAskedToSkip ? 'Skip requested ✓' : 'Skip to the end'}
          </button>
          <span className="font-display text-[0.6rem] uppercase tracking-wide text-ec-bone-dim">
            {youAskedToSkip
              ? `waiting on ${label(otherFinalist)}`
              : `place the last ${countOf(unplaced.length, 'ball')} at once`}
          </span>
        </div>
      )}
    </div>
  );
}

function Row({
  title,
  tone,
  entries,
  capacity
}: {
  title: string;
  tone: 'brass' | 'slate';
  entries: Array<string | number | null>;
  capacity: number;
}) {
  const slots = Array.from({ length: capacity }, (_, index) => entries[index] ?? null);
  return (
    <div
      className={`rounded-lg border p-2 ${
        tone === 'brass' ? 'border-ec-brass/50 bg-ec-brass/5' : 'border-ec-line bg-ec-panel'
      }`}
    >
      <p
        className={`mb-1 font-display text-[0.6rem] uppercase tracking-wide ${
          tone === 'brass' ? 'text-ec-brass' : 'text-ec-bone-dim'
        }`}
      >
        {title}
      </p>
      <div className="flex flex-wrap gap-1">
        {slots.map((entry, index) =>
          entry === null ? (
            <div
              key={`empty-${index}`}
              className="h-12 w-12 shrink-0 rounded-full border border-dashed border-ec-line"
            />
          ) : (
            <Ball key={`${entry}-${index}`} claim={entry as number | 'KILLER'} size="sm" />
          )
        )}
      </div>
    </div>
  );
}
